import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Trophy, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import Navigation from '@/components/Navigation';
import LeagueCard from '@/components/LeagueCard';
import { useStore } from '@/lib/store';
import * as api from '@/lib/api';
import { isValidSlug } from '@/lib/league-slugs';
import { supabase } from '@/lib/supabase';
import type { League } from '@/lib/mock-data';

type LeagueSeason = {
  id: string;
  league_id: string;
  name: string;
  description?: string;
  created_at: string;
  sort_index?: number;
};

const Leagues = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useStore();
  
  // Get league slug from URL if we're in a league route
  // Supports both /BLISLeague/leagues and /BLISLeague patterns
  const leagueSlug = useMemo(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    // Check if first part is a valid league slug (e.g., /BLISLeague or /BLISLeague/leagues)
    if (pathParts.length > 0 && isValidSlug(pathParts[0])) {
      return pathParts[0];
    }
    return null;
  }, [location.pathname]);

  const [league, setLeague] = useState<League | null>(null);
  const [leagueName, setLeagueName] = useState<string | null>(null);
  const [seasons, setSeasons] = useState<LeagueSeason[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const toKebabSlug = (value: string) =>
    value
      .replace(/([a-z])([A-Z])/g, "$1-$2")
      .replace(/[_\s]+/g, "-")
      .replace(/-+/g, "-")
      .toLowerCase();

  const derivedLeagueName = useMemo(() => {
    if (!leagueSlug) return null;
    return leagueSlug
      .replace(/[_-]+/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .trim();
  }, [leagueSlug]);

  useEffect(() => {
    const loadLeagueData = async () => {
      setIsLoading(true);
      try {
        if (leagueSlug) {
          // Find league by slug or name (case-insensitive)
          let foundLeague: League | null = null;
          const slugCandidates = Array.from(new Set([leagueSlug, toKebabSlug(leagueSlug)]));
          const orClause = slugCandidates.map((slug) => `slug.ilike.${slug}`).join(',');
          const { data: leagueBySlug, error: leagueBySlugError } = await supabase
            .from('leagues')
            .select('id, name')
            .or(orClause)
            .maybeSingle();
          if (leagueBySlugError) {
            console.warn('[Leagues] Failed to fetch league by slug:', leagueBySlugError);
          }
          if (leagueBySlug?.id) {
            foundLeague = await api.leagueAPI.getLeague(leagueBySlug.id);
          }

          if (!foundLeague && derivedLeagueName) {
            const { data: leagueByName, error: leagueByNameError } = await supabase
              .from('leagues')
              .select('id, name')
              .ilike('name', derivedLeagueName)
              .maybeSingle();
            if (leagueByNameError) {
              console.warn('[Leagues] Failed to fetch league by name:', leagueByNameError);
            }
            if (leagueByName?.id) {
              foundLeague = await api.leagueAPI.getLeague(leagueByName.id);
            }
          }
          
          if (foundLeague) {
            setLeague(foundLeague);
            setLeagueName(foundLeague.name);
            
            // Fetch seasons for this league
            const { data: seasonsData, error } = await supabase
              .from('seasons')
              .select('*')
              .eq('league_id', foundLeague.id)
              .order('sort_index', { ascending: false, nullsLast: true })
              .order('created_at', { ascending: false });
            
            if (error) {
              console.error('Error fetching seasons:', error);
              setSeasons([]);
            } else {
              // Convert to Season format
              const convertedSeasons: LeagueSeason[] = (seasonsData || []).map((dbSeason: any) => ({
                id: dbSeason.id,
                league_id: dbSeason.league_id,
                name: dbSeason.name,
                description: dbSeason.description || undefined,
                created_at: dbSeason.created_at,
                sort_index: dbSeason.sort_index,
              }));
              setSeasons(convertedSeasons);
            }
          } else {
            console.warn(`League "${leagueName}" not found`);
            setLeague(null);
            setSeasons([]);
          }
        } else {
          // No league slug - show nothing or redirect?
          setLeague(null);
          setSeasons([]);
        }
      } catch (error) {
        console.error('Error loading league data:', error);
        setLeague(null);
        setSeasons([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadLeagueData();
  }, [leagueSlug, derivedLeagueName]);

  // If no league slug, redirect to home
  if (!leagueSlug || !leagueName) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="pt-6 text-center">
              <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No League Selected</h3>
              <p className="text-muted-foreground">
                Please access leagues via their URL (e.g., /MastersOfMusic or /BLISLeague)
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {league?.name || leagueName}
          </h1>
          <p className="text-muted-foreground">
            {league?.description || 'View seasons and rounds from this league.'}
          </p>
        </div>

        <div className="space-y-6">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading league data...</p>
            </div>
          ) : !league ? (
            <Card>
              <CardContent className="pt-6 text-center">
                <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">League Not Found</h3>
                <p className="text-muted-foreground">
                  The league "{leagueName}" could not be found.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* League Info Card */}
              <Card>
                <CardHeader>
                  <CardTitle>League Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">League Name</p>
                      <p className="font-medium">{league.name}</p>
                    </div>
                    {league.description && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Description</p>
                        <p className="font-medium">{league.description}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Seasons List */}
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">Seasons</h2>
                {seasons.length === 0 ? (
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">No Seasons</h3>
                      <p className="text-muted-foreground">
                        This league doesn't have any seasons yet.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-6 md:grid-cols-2">
                    {seasons.map((season) => (
                      <Card 
                        key={season.id} 
                        className="cursor-pointer hover:shadow-lg transition-shadow" 
                        onClick={() => {
                          if (leagueSlug) {
                            navigate(`/${leagueSlug}/seasons/${season.id}`);
                          } else {
                            navigate(`/seasons/${season.id}`);
                          }
                        }}
                      >
                        <CardHeader>
                          <CardTitle className="text-2xl md:text-3xl font-bold">{season.name}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {season.description && (
                            <p className="text-sm text-muted-foreground mb-4">{season.description}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Leagues;
